import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Student } from '@/interface/types/user'
import { studentService } from '@/lib/api/services/student-service'
import { parentService } from '@/lib/api/services/parent-service'
import { classService } from '@/lib/api/services/class-service'
import { jsPDF } from 'jspdf'
import { StudentManagementTour } from '@/tour/admin/studentManagementTour'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { X, Filter, ChevronLeft, ChevronRight, Download, RefreshCw, Trash2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { StudentForm, type StudentFormValues } from './StudentForm'
import Loading from '@/components/shared/Loading'
import CdnImage from '@/components/shared/CdnImage'
import { DEFAULT_AVATAR } from '@/constants'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ExtendedParent as Parent } from '@/interface/types/parent-extended'
import { Class } from '@/interface/types'
import { useToast } from '@/components/ui/use-toast'
import { BulkStudentImport } from './BulkStudentImport'
import { useTranslation } from 'react-i18next'
// Translation imports removed - using hardcoded English text

// Define interfaces for filters
interface StudentFilters {
  gradeLevel: string
  gender: string
  isPaid: string
  class: string
  parentId: string
}

interface ActiveFilter {
  key: keyof StudentFilters
  value: string
  label: string
}

// Utility function to generate PDF content for students
const generateStudentPDF = (students: Student[], month: string, year: string, t: any, onlyPaid?: boolean, onlyUnpaid?: boolean) => {
  // Create PDF document
  const doc = new jsPDF()
  // Add title
  let title = t('student-folder.studentManagement.pdf.titles.all')
  if (onlyPaid) title = t('student-folder.studentManagement.pdf.titles.paid')
  if (onlyUnpaid) title = t('student-folder.studentManagement.pdf.titles.unpaid')
  doc.setFontSize(18)
  doc.text(`${title} - ${month} ${year}`, 14, 22)
  doc.setFontSize(10)
  doc.text(`${t('student-folder.studentManagement.pdf.generatedOn', { date: new Date().toLocaleDateString() })}`, 14, 30)
  let filteredStudents = students
  if (onlyPaid) {
    filteredStudents = students.filter(student => student.isPaid === true)
  } else if (onlyUnpaid) {
    filteredStudents = students.filter(student => student.isPaid === false)
  }
  const margin = 14
  const pageWidth = doc.internal.pageSize.width
  const pageHeight = doc.internal.pageSize.height
  const tableWidth = pageWidth - (margin * 2)
  const rowHeight = 10
  const colWidths = [50, 25, 30, 40, 30]
  const headers = [
    t('student-folder.studentManagement.pdf.headers.name'),
    t('student-folder.studentManagement.pdf.headers.cin'),
    t('student-folder.studentManagement.pdf.headers.gradeLevel'),
    t('student-folder.studentManagement.pdf.headers.class'),
    t('student-folder.studentManagement.pdf.headers.paymentStatus')
  ]
  doc.setFillColor(240, 240, 240)
  doc.rect(margin, 35, tableWidth, rowHeight, 'F')
  doc.setFont('helvetica', 'bold')
  doc.setFontSize(10)
  let xPos = margin
  headers.forEach((header, i) => {
    doc.text(header, xPos + 2, 35 + rowHeight/2 + 2)
    xPos += colWidths[i]
  })
  let yPos = 35 + rowHeight
  doc.setFont('helvetica', 'normal')
  filteredStudents.forEach((student, index) => {
    if (yPos > pageHeight - 20) {
      doc.addPage()
      yPos = 20
      doc.setFillColor(240, 240, 240)
      doc.rect(margin, yPos, tableWidth, rowHeight, 'F')
      doc.setFont('helvetica', 'bold')
      xPos = margin
      headers.forEach((header, i) => {
        doc.text(header, xPos + 2, yPos + rowHeight/2 + 2)
        xPos += colWidths[i]
      })
      yPos += rowHeight
      doc.setFont('helvetica', 'normal')
    }
    if (index % 2 === 0) {
      doc.setFillColor(250, 250, 250)
      doc.rect(margin, yPos, tableWidth, rowHeight, 'F')
    }
    const statusX = margin + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3]
    const statusWidth = colWidths[4]
    if (student.isPaid) {
      doc.setFillColor(204, 229, 255)
    } else {
      doc.setFillColor(255, 243, 205)
    }
    doc.rect(statusX, yPos, statusWidth, rowHeight, 'F')
    const className = typeof student.class === 'string'
      ? student.class
      : (student.class as any)?.name || t('student-folder.studentManagement.pdf.noClass')
    xPos = margin
    doc.text(`${student.firstname} ${student.lastname}`.substring(0, 24), xPos + 2, yPos + rowHeight/2 + 2)
    xPos += colWidths[0]
    doc.text((student.cin || '').substring(0, 10), xPos + 2, yPos + rowHeight/2 + 2)
    xPos += colWidths[1]
    doc.text((student.gradeLevel || t('student-folder.studentManagement.pdf.noGradeLevel')).substring(0, 12), xPos + 2, yPos + rowHeight/2 + 2)
    xPos += colWidths[2]
    doc.text(className.substring(0, 18), xPos + 2, yPos + rowHeight/2 + 2)
    xPos += colWidths[3]
    doc.text(student.isPaid ? t('student-folder.studentManagement.pdf.status.paid') : t('student-folder.studentManagement.pdf.status.unpaid'), xPos + 2, yPos + rowHeight/2 + 2)
    yPos += rowHeight
  })
  return doc
}

// Utility function to download PDF file
const downloadPDF = (doc: any, filename: string) => {
  doc.save(`${filename}.pdf`)
}

export default function StudentManagement(): JSX.Element {
  const { t } = useTranslation('admin')
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null)
  const [isProfileModalOpen, setIsProfileModalOpen] = useState<boolean>(false)
  const [isAddStudentOpen, setIsAddStudentOpen] = useState<boolean>(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [studentToTogglePaid, setStudentToTogglePaid] =
    useState<Student | null>(null)
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false)
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)
  const [resetMonth, setResetMonth] = useState('')
  const [resetYear, setResetYear] = useState('')
  const [isResetting, setIsResetting] = useState(false)

  const [activeFilters, setActiveFilters] = useState<ActiveFilter[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [parentSearchQuery, setParentSearchQuery] = useState('')
  const [showLoadingForEmpty, setShowLoadingForEmpty] = useState(false)

  const { toast } = useToast()
  const queryClient = useQueryClient()

  // State for download dialogs
  const [isAllStudentsDialogOpen, setIsAllStudentsDialogOpen] = useState(false)
  const [isPaidStudentsDialogOpen, setIsPaidStudentsDialogOpen] = useState(false)
  const [isUnpaidStudentsDialogOpen, setIsUnpaidStudentsDialogOpen] = useState(false)
  const [downloadMonth, setDownloadMonth] = useState('')
  const [downloadYear, setDownloadYear] = useState('')
  const [downloadFilename, setDownloadFilename] = useState('')
  const [isDownloading, setIsDownloading] = useState(false)

  // State for delete student
  const [studentToDelete, setStudentToDelete] = useState<Student | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  // Queries with improved error handling
  const {
    data: students = [],
    isLoading: isLoadingStudents,
    isError: isStudentsError,
    // error: studentsError,
  } = useQuery({
    queryKey: ['students'],
    queryFn: async () => {
      try {
     // console.logFetching students...')
        const result = await studentService.getAll()
     // console.logStudents fetched:', result?.length || 0, 'students')
        return Array.isArray(result) ? result : []
      } catch (error) {
        // console.error('Error fetching students:', error)
        throw error
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 3, // Retry up to 3 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    refetchOnWindowFocus: true, // Refetch when window regains focus
  })

  // Show loading for 1 second when no students are found
  useEffect(() => {
    if (!isLoadingStudents && (!students || students.length === 0)) {
      setShowLoadingForEmpty(true)
      const timer = setTimeout(() => {
        setShowLoadingForEmpty(false)
      }, 1000)
      return () => clearTimeout(timer)
    } else {
      setShowLoadingForEmpty(false)
    }
  }, [isLoadingStudents, students])

  const {
    data: parents = [],
    isLoading: isLoadingParents,
    isError: isParentsError,
    // error: parentsError,
  } = useQuery({
    queryKey: ['parents'],
    queryFn: parentService.getAll,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const {
    data: classes = [],
    isLoading: isLoadingClasses,
    isError: isClassesError,
    // error: classesError,
  } = useQuery({
    queryKey: ['classes'],
    queryFn: classService.getAll,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  // Mutations
  const updateStudentMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Student> }) =>
      studentService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] })
      setIsProfileModalOpen(false)
    },
  })

  const createStudentMutation = useMutation({
    mutationFn: (data: Partial<Student>) => studentService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] })
      setIsAddStudentOpen(false)
    },
  })

  const togglePaymentStatusMutation = useMutation({
    mutationFn: ({ id, isPaid }: { id: string; isPaid: boolean }) =>
      studentService.update(id, { isPaid }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] })
      setIsPaymentDialogOpen(false)
      setStudentToTogglePaid(null)
    },
  })

  // Mutation for resetting all students to unpaid
  const resetAllStudentsMutation = useMutation({
    mutationFn: async () => {
      // Create an array of promises for each student update
      const updatePromises = students.map(student =>
        studentService.update(student.id, { isPaid: false })
      )

      // Wait for all updates to complete
      return Promise.all(updatePromises)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] })
      setIsResetting(false)
      setIsResetDialogOpen(false)

      toast({
        title: t('student-folder.studentManagement.toast.resetConfirmation.title'),
        description: t('student-folder.studentManagement.toast.resetConfirmation.description', { month: resetMonth, year: resetYear }),
      })

      // Generate and download the report after reset
      const pdfDoc = generateStudentPDF(students, resetMonth, resetYear, t)
      downloadPDF(pdfDoc, `student_payment_status_${resetMonth}_${resetYear}`)
    },
    onError: () => {
      // console.error('Failed to reset students:', error)
      setIsResetting(false)

      toast({
        title: t('student-folder.studentManagement.toast.downloadFailed.title'),
        description: t('student-folder.studentManagement.toast.downloadFailed.description', { month: resetMonth, year: resetYear }),
        variant: "destructive"
      })
    }
  })
  // Handler for opening the delete confirmation dialog
  const handleDeleteClick = (student: Student) => {
    setStudentToDelete(student)
    setIsDeleteDialogOpen(true)
  }

  // Handler for confirming student deletion
  const confirmDeleteStudent = () => {
    if (studentToDelete) {
      setIsDeleting(true)
      deleteStudentMutation.mutate(studentToDelete.id)
    }
  }

  // Delete student mutation
  const deleteStudentMutation = useMutation({
    mutationFn: (id: string) => studentService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['students'] })
      setIsDeleteDialogOpen(false)
      setStudentToDelete(null)
      setIsDeleting(false)
      
      toast({
        title: t('student-folder.studentManagement.toast.studentDeleted.title'),
        description: t('student-folder.studentManagement.toast.studentDeleted.description'),
        variant: "default",
      })
    },
    onError: () => {
      // console.error('Error deleting student:', error)
      setIsDeleting(false)
      
      toast({
        title: t('student-folder.studentManagement.toast.deletionFailed.title'),
        description: t('student-folder.studentManagement.toast.deletionFailed.description'),
        variant: "destructive",
      })
    }
  })

  // Get unique values for select filters
  const uniqueGradeLevels = [
    ...new Set(students?.map((s) => s.gradeLevel) || []),
  ]

  // Get unique class names
  const uniqueClasses = classes ? [...new Set(classes.map((c) => c.name))] : []

  // Filter parents based on search query
  const filteredParents = parents.filter((parent) => {
    if (!parentSearchQuery) return true

    const fullName = `${parent.firstname} ${parent.lastname}`.toLowerCase()
    const cin = parent.cin?.toLowerCase() || ''
    const query = parentSearchQuery.toLowerCase()

    return fullName.includes(query) || cin.includes(query)
  })

  // Ensure students is always an array and has valid data
  const validStudents = Array.isArray(students) ? students.filter(student => 
    student && 
    student.id && 
    (student.firstname || student.lastname)
  ) : []

  // console.log('Valid students for display:', validStudents.length)

  // Filter and search students with improved debugging
  const filteredStudents = validStudents.filter((student) => {
    // Search query matching
    const searchMatches =
      searchQuery === '' ||
      `${student.firstname} ${student.lastname} ${student.cin}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase())

    // All active filters must match
    const filtersMatch = activeFilters.every((filter) => {
      switch (filter.key) {
        case 'gradeLevel':
          return student.gradeLevel === filter.value
        case 'gender':
          return student.gender?.toLowerCase() === filter.value.toLowerCase()
        case 'isPaid':
          return student.isPaid === (filter.value === 'true')
        case 'class':
          // Handle class filtering - check if class name matches
          const className =
            typeof student.class === 'string'
              ? student.class
              : (student.class as any)?.name || ''
          return className === filter.value
        case 'parentId':
          // Handle parent filtering - check if parent ID matches
          if (!student.parent) return false;
          
          // Check for nested parent structure
          if (typeof student.parent === 'object') {
            // Check if parent has id directly or through user object
            return student.parent.id === filter.value || 
                   student.parent.userId === filter.value ||
                   (student.parent.user && student.parent.user.id === filter.value);
          }
          
          // Handle string parent ID
          return student.parent === filter.value;
        default:
          return true
      }
    })

    return searchMatches && filtersMatch
  })

  // console.log('Filtered students:', filteredStudents.length, 'out of', validStudents.length, 'total')
  // console.log('Search query:', searchQuery)
  // console.log('Active filters:', activeFilters.length)

  // Pagination setup
  const studentsPerPage = 5
  const totalPages = Math.ceil(filteredStudents.length / studentsPerPage)
  const indexOfLastStudent = currentPage * studentsPerPage
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage
  const currentStudents = filteredStudents.slice(
    indexOfFirstStudent,
    indexOfLastStudent
  )

  const handleShowProfile = (student: Student) => {
    setSelectedStudent(student)
    setIsProfileModalOpen(true)
  }

  const handleFilterChange = (
    key: keyof StudentFilters,
    value: string,
    label: string
  ) => {
    setActiveFilters((prev) => {
      const filtered = prev.filter((f) => f.key !== key)
      if (value !== 'all') {
        return [...filtered, { key, value, label }]
      }
      return filtered
    })
    setCurrentPage(1)
  }

  const clearAllFilters = () => {
    setActiveFilters([])
    setSearchQuery('')
    setParentSearchQuery('')
    setCurrentPage(1)
  }

  // Adjust current page if it exceeds total pages after deletion
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages)
    } else if (totalPages === 0) {
      setCurrentPage(1)
    }
  }, [totalPages, currentPage])

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber)
  }

  const handleTogglePaymentStatus = (student: Student) => {
    setStudentToTogglePaid(student)
    setIsPaymentDialogOpen(true)
  }

  const confirmTogglePayment = () => {
    if (studentToTogglePaid) {
      togglePaymentStatusMutation.mutate({
        id: studentToTogglePaid.id,
        isPaid: !studentToTogglePaid.isPaid,
      })
    }
  }

  // Handler for opening the all students download dialog
  const handleOpenAllStudentsDialog = () => {
    const currentDate = new Date()
    setDownloadMonth(currentDate.toLocaleString('default', { month: 'long' }))
    setDownloadYear(currentDate.getFullYear().toString())
    setDownloadFilename(`all_students_${currentDate.toLocaleString('default', { month: 'long' })}_${currentDate.getFullYear()}`)
    setIsAllStudentsDialogOpen(true)
  }

  // Handler for opening the paid students download dialog
  const handleOpenPaidStudentsDialog = () => {
    const currentDate = new Date()
    setDownloadMonth(currentDate.toLocaleString('default', { month: 'long' }))
    setDownloadYear(currentDate.getFullYear().toString())
    setDownloadFilename(`paid_students_${currentDate.toLocaleString('default', { month: 'long' })}_${currentDate.getFullYear()}`)
    setIsPaidStudentsDialogOpen(true)
  }

  // Handler for opening the unpaid students download dialog
  const handleOpenUnpaidStudentsDialog = () => {
    const currentDate = new Date()
    setDownloadMonth(currentDate.toLocaleString('default', { month: 'long' }))
    setDownloadYear(currentDate.getFullYear().toString())
    setDownloadFilename(`unpaid_students_${currentDate.toLocaleString('default', { month: 'long' })}_${currentDate.getFullYear()}`)
    setIsUnpaidStudentsDialogOpen(true)
  }

  // Handler for confirming the download all students operation
  const confirmDownloadAllStudents = () => {
    // Validate inputs
    if (!downloadMonth.trim() || !downloadYear.trim() || !downloadFilename.trim()) {
      toast({
        title: t('student-folder.studentManagement.toast.validationError.title'),
        description: t('student-folder.studentManagement.toast.validationError.description'),
        variant: "destructive"
      })
      return
    }

    // Validate year format (4 digits)
    if (!/^\d{4}$/.test(downloadYear)) {
      toast({
        title: t('student-folder.studentManagement.toast.yearFormatError.title'),
        description: t('student-folder.studentManagement.toast.yearFormatError.description'),
        variant: "destructive"
      })
      return
    }

    setIsDownloading(true)
    
    try {
      const pdfDoc = generateStudentPDF(students, downloadMonth, downloadYear, t)
      downloadPDF(pdfDoc, downloadFilename)
      
      toast({
        title: t('student-folder.studentManagement.toast.downloadStarted.title'),
        description: t('student-folder.studentManagement.toast.downloadStarted.description'),
      })
    } catch (error) {
      // console.error('Download error:', error)
      toast({
        title: t('student-folder.studentManagement.toast.downloadFailed.title'),
        description: t('student-folder.studentManagement.toast.downloadFailed.description'),
        variant: "destructive"
      })
    } finally {
      setIsDownloading(false)
      setIsAllStudentsDialogOpen(false)
    }
  }

  // Handler for confirming the download paid students operation
  const confirmDownloadPaidStudents = () => {
    // Validate inputs
    if (!downloadMonth.trim() || !downloadYear.trim() || !downloadFilename.trim()) {
      toast({
        title: t('student-folder.studentManagement.toast.validationError.title'),
        description: t('student-folder.studentManagement.toast.validationError.description'),
        variant: "destructive"
      })
      return
    }

    // Validate year format (4 digits)
    if (!/^\d{4}$/.test(downloadYear)) {
      toast({
        title: t('student-folder.studentManagement.toast.yearFormatError.title'),
        description: t('student-folder.studentManagement.toast.yearFormatError.description'),
        variant: "destructive"
      })
      return
    }

    setIsDownloading(true)
    
    try {
      const pdfDoc = generateStudentPDF(students, downloadMonth, downloadYear, t, true)
      downloadPDF(pdfDoc, downloadFilename)
      
      toast({
        title: t('student-folder.studentManagement.toast.downloadStarted.title'),
        description: t('student-folder.studentManagement.toast.downloadStarted.description'),
      })
    } catch (error) {
      // console.error('Download error:', error)
      toast({
        title: t('student-folder.studentManagement.toast.downloadFailed.title'),
        description: t('student-folder.studentManagement.toast.downloadFailed.description'),
        variant: "destructive"
      })
    } finally {
      setIsDownloading(false)
      setIsPaidStudentsDialogOpen(false)
    }
  }

  // Handler for confirming the download unpaid students operation
  const confirmDownloadUnpaidStudents = () => {
    // Validate inputs
    if (!downloadMonth.trim() || !downloadYear.trim() || !downloadFilename.trim()) {
      toast({
        title: t('student-folder.studentManagement.toast.validationError.title'),
        description: t('student-folder.studentManagement.toast.validationError.description'),
        variant: "destructive"
      })
      return
    }

    // Validate year format (4 digits)
    if (!/^\d{4}$/.test(downloadYear)) {
      toast({
        title: t('student-folder.studentManagement.toast.yearFormatError.title'),
        description: t('student-folder.studentManagement.toast.yearFormatError.description'),
        variant: "destructive"
      })
      return
    }

    setIsDownloading(true)
    
    try {
      const pdfDoc = generateStudentPDF(students, downloadMonth, downloadYear, t, false, true)
      downloadPDF(pdfDoc, downloadFilename)
      
      toast({
        title: t('student-folder.studentManagement.toast.downloadStarted.title'),
        description: t('student-folder.studentManagement.toast.downloadStarted.description'),
      })
    } catch (error) {
      // console.error('Download error:', error)
      toast({
        title: t('student-folder.studentManagement.toast.downloadFailed.title'),
        description: t('student-folder.studentManagement.toast.downloadFailed.description'),
        variant: "destructive"
      })
    } finally {
      setIsDownloading(false)
      setIsUnpaidStudentsDialogOpen(false)
    }
  }

  // Handler for opening the reset dialog
  const handleOpenResetDialog = () => {
    const currentDate = new Date()
    setResetMonth(currentDate.toLocaleString('default', { month: 'long' }))
    setResetYear(currentDate.getFullYear().toString())
    setIsResetDialogOpen(true)
  }

  // Handler for confirming the reset operation
  const confirmResetAllStudents = () => {
    // Validate month and year
    if (!resetMonth.trim() || !resetYear.trim()) {
      toast({
        title: t('student-folder.studentManagement.toast.validationError.title'),
        description: t('student-folder.studentManagement.toast.validationError.description'),
        variant: "destructive"
      })
      return
    }

    // Validate year format (4 digits)
    if (!/^\d{4}$/.test(resetYear)) {
      toast({
        title: t('student-folder.studentManagement.toast.yearFormatError.title'),
        description: t('student-folder.studentManagement.toast.yearFormatError.description'),
        variant: "destructive"
      })
      return
    }

    setIsResetting(true)
    resetAllStudentsMutation.mutate()
  }

  const handleUpdateStudent = async (data: StudentFormValues) => {
    if (!selectedStudent?.id) return
    try {
      const updateData: any = {
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
        cin: data.cin,
        gradeLevel: data.gradeLevel,
        parentId: data.parentId || undefined,
        classId: data.classId || undefined,
        gender: data.gender,
        birthday: data.birthday?.toISOString(),
        address: data.address,
        phone: data.phone,
        nationality: data.nationality || 'tunisian',
        avatar: data.avatar,
        isActive: data.isActive,
      }

      // Add password to update data if provided
      if (data.password && data.password.trim() !== '') {
        updateData.password = data.password;
      }

      // If the classId field is empty or undefined, remove it from the request
      if (!updateData.classId) {
        delete updateData.classId
      }

      // If the parentId field is empty or undefined, remove it from the request
      if (!updateData.parentId) {
        delete updateData.parentId
      }

   // console.logUpdating student with data:', updateData);

      await updateStudentMutation.mutateAsync({
        id: selectedStudent.id,
        data: updateData,
      })
    } catch (error) {
      // console.error('Failed to update student:', error)
    }
  }

  const handleCreateStudent = async (data: StudentFormValues) => {
    try {
      if (!data.password) {
        throw new Error(t('student-folder.studentManagement.toast.passwordRequired.description'))
      }

      // Format the data according to the API requirements
      const createData: any = {
        firstname: data.firstname,
        lastname: data.lastname,
        email: data.email,
        password: data.password,
        cin: data.cin,
        gradeLevel: data.gradeLevel || '',
        enrolledDate:
          data.enrolledDate?.toISOString() || new Date().toISOString(),
        // Use the parentId directly as a string
        parentId: data.parentId || undefined,
        // Use the classId directly as a string for the class ID
        classId: data.classId || undefined,
        gender:
          data.gender.charAt(0).toUpperCase() +
          data.gender.slice(1).toLowerCase(), // Capitalize first letter
        birthday: data.birthday?.toISOString() || null,
        address: data.address || '',
        phone: data.phone || '',
        avatar: data.avatar || DEFAULT_AVATAR,
        isActive: data.isActive,
        isPaid: true, // Default to true, can be modified if needed
        role: 'Student', // Set the role with correct capitalization
      }

      // If the classId field is empty or undefined, remove it from the request
      if (!createData.classId) {
        delete createData.classId
      }

      // If the parentId field is empty or undefined, remove it from the request
      if (!createData.parentId) {
        delete createData.parentId
      }

      // Log the data being sent to the API
      // console.log(
      //   'Creating student with data:',
      //   JSON.stringify(createData, null, 2)
      // )
      await createStudentMutation.mutateAsync(createData)
    } catch (error: any) {
      // console.error('Failed to create student:', error)
      
      let errorTitle = t('student-folder.studentManagement.toast.creationFailed.title') || 'Failed to Create Student'
      let errorDescription = ''
      
      // Better error handling for API responses
      if (error.response?.data) {
        const apiError = error.response.data
        let backendMessage = ''
        
        // Extract the backend error message
        if (typeof apiError.message === 'string') {
          backendMessage = apiError.message
        } else if (Array.isArray(apiError.message)) {
          // Handle array of error objects
          const errorMessages = apiError.message.map((err: any) => {
            if (typeof err === 'string') return err
            if (err.field && err.message) return `${err.field}: ${err.message}`
            return JSON.stringify(err)
          })
          backendMessage = errorMessages.join(', ')
        } else if (apiError.message && typeof apiError.message === 'object') {
          // Handle validation errors object
          const validationErrors = []
          
          if (apiError.message.email) {
            validationErrors.push(apiError.message.email)
          }
          if (apiError.message.cin) {
            validationErrors.push(apiError.message.cin)
          }
          if (apiError.message.gradeLevel) {
            validationErrors.push(apiError.message.gradeLevel)
          }
          if (apiError.message.class) {
            validationErrors.push(apiError.message.class)
          }
          
          backendMessage = validationErrors.join('. ')
        } else if (apiError.error === 'CONFLICT') {
          backendMessage = 'A student with this information already exists'
        } else if (apiError.error === 'BAD_REQUEST') {
          backendMessage = 'Invalid data provided'
        } else if (apiError.error) {
          backendMessage = apiError.error
        }
        
        // Detect if it's a CIN or email error and add translated prefix
        const lowerMessage = backendMessage.toLowerCase()
        if (lowerMessage.includes('cin') || lowerMessage.includes('c.i.n')) {
          errorDescription = `${t('student-folder.studentManagement.toast.errors.cinExists')}`
        } else if (lowerMessage.includes('email') || lowerMessage.includes('e-mail')) {
          errorDescription = `${t('student-folder.studentManagement.toast.errors.emailExists')}`
        } else {
          // Use backend message as is
          errorDescription = backendMessage || t('student-folder.studentManagement.toast.creationFailed.description') || 'An error occurred while creating the student'
        }
      } else if (error.message) {
        // Check error.message for CIN/email keywords
        const lowerMessage = error.message.toLowerCase()
        if (lowerMessage.includes('cin') || lowerMessage.includes('c.i.n')) {
          errorDescription = `${t('student-folder.studentManagement.toast.errors.cinExists') || 'CIN already exists'}: ${error.message}`
        } else if (lowerMessage.includes('email') || lowerMessage.includes('e-mail')) {
          errorDescription = `${t('student-folder.studentManagement.toast.errors.emailExists') || 'Email already exists'}: ${error.message}`
        } else {
          errorDescription = error.message
        }
      } else {
        errorDescription = t('student-folder.studentManagement.toast.creationFailed.description') || 'An error occurred while creating the student'
      }
      
      // Show error toast with proper styling
      toast({
        title: errorTitle,
        description: errorDescription,
        variant: "destructive"
      })
    }
  }

  // Function to render a student row without using hooks inside
  const renderStudentRow = (student: Student) => {
    // We don't use the unread count feature for now to avoid hook issues

    return (
      <TableRow key={student.id}>
        <TableCell>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full overflow-hidden">
              <CdnImage
                src={student.avatar || DEFAULT_AVATAR}
                alt={`${student.firstname} ${student.lastname}`}
                className="w-full h-full object-cover"
              />
            </div>
            <span>
              {student.firstname} {student.lastname}
            </span>
          </div>
        </TableCell>
        <TableCell>{student.cin}</TableCell>
        <TableCell>{student.gradeLevel || t('student-folder.studentManagement.pdf.noGradeLevel')}</TableCell>
        <TableCell>
          {typeof student.class === 'string'
            ? student.class
            : (student.class as any)?.name || t('student-folder.studentManagement.pdf.noClass')}
        </TableCell>
        <TableCell>
          <Badge variant={student.isPaid ? 'success' : 'destructive'}>
            {student.isPaid ? t('student-folder.studentManagement.pdf.status.paid') : t('student-folder.studentManagement.pdf.status.unpaid')}
          </Badge>
        </TableCell>
        <TableCell>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => handleShowProfile(student)}
            >
              {t('student-folder.studentManagement.buttons.profile')}
            </Button>

            <Button
              variant={student.isPaid ? 'destructive' : 'default'}
              onClick={() => handleTogglePaymentStatus(student)}
            >
              {student.isPaid
                ? t('student-folder.studentManagement.buttons.markAsUnpaid')
                : t('student-folder.studentManagement.buttons.markAsPaid')}
            </Button>

            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleDeleteClick(student)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    )
  }



  if (isLoadingStudents || isLoadingParents || isLoadingClasses || showLoadingForEmpty)
    return <Loading />
  
  // Show error states with better handling
  if (isStudentsError || isParentsError || isClassesError) {
    return (
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          <h3 className="font-semibold mb-2">Failed to Load Data</h3>
          <p className="text-sm mb-2">
            {isStudentsError && 'Students: Failed to load students'}
            {isStudentsError && (isParentsError || isClassesError) && ' | '}
            {isParentsError && 'Parents: Failed to load parents'}
            {isParentsError && isClassesError && ' | '}
            {isClassesError && 'Classes: Failed to load classes'}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6" data-tour="student-header">
        <h2 className="text-2xl font-bold">{t('student-folder.studentManagement.title')}</h2>
        <div className="flex items-center gap-2">
          <BulkStudentImport />
          <Button 
            onClick={() => setIsAddStudentOpen(true)}
            data-tour="add-student-button"
          >
            {t('student-folder.studentManagement.buttons.addStudent')}
          </Button>
          <Badge variant="secondary" className="text-sm">
            {validStudents?.length || 0} {t('student-folder.studentManagement.badges.studentCount')}
          </Badge>
        </div>
      </div>

      {/* Export and Reset Buttons */}
      <div className="flex flex-wrap gap-2 mb-6 p-4 bg-gray-50 rounded-lg" data-tour="payment-management">
        <div className="w-full mb-2">
          <h3 className="text-lg font-semibold">{t('student-folder.studentManagement.paymentManagement.title')}</h3>
        </div>
        <Button
          variant="outline"
          className="flex items-center gap-2"
          onClick={handleOpenAllStudentsDialog}
          data-tour="download-payments"
        >
          <Download className="h-4 w-4" />
          {t('student-folder.studentManagement.paymentManagement.buttons.downloadAll')}
        </Button>
        <Button
          variant="outline"
          className="flex items-center gap-2 bg-blue-50"
          onClick={handleOpenPaidStudentsDialog}
        >
          <Download className="h-4 w-4" />
          {t('student-folder.studentManagement.paymentManagement.buttons.downloadPaid')}
        </Button>
        <Button
          variant="outline"
          className="flex items-center gap-2 bg-yellow-50"
          onClick={handleOpenUnpaidStudentsDialog}
        >
          <Download className="h-4 w-4" />
          {t('student-folder.studentManagement.paymentManagement.buttons.downloadUnpaid')}
        </Button>
        <Button
          variant="outline"
          className="flex items-center gap-2 ml-auto bg-red-50 hover:bg-red-100"
          onClick={handleOpenResetDialog}
        >
          <RefreshCw className="h-4 w-4" />
          {t('student-folder.studentManagement.paymentManagement.buttons.resetPayments')}
        </Button>
      </div>

      <div className="flex gap-4 mb-6">
        <div className="flex-1" data-tour="student-search">
          <Input
            placeholder={t('student-folder.studentManagement.search.placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full"
          />
        </div>
        <div data-tour="student-filters">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="gap-2">
                <Filter className="h-4 w-4" />
                {t('student-folder.studentManagement.filter.button')}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-4">
                <h4 className="font-medium">{t('student-folder.studentManagement.filter.title')}</h4>
                <div className="space-y-2">
                  <Label htmlFor="gradeLevel-filter">{t('student-folder.studentManagement.filter.fields.gradeLevel.label')}</Label>
                  <Select
                    onValueChange={(value) =>
                      handleFilterChange('gradeLevel', value, `${t('student-folder.studentManagement.filter.fields.gradeLevel.label')}: ${value}`)
                    }
                    defaultValue="all"
                  >
                    <SelectTrigger id="gradeLevel-filter">
                      <SelectValue placeholder={t('student-folder.studentManagement.filter.fields.gradeLevel.label')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('student-folder.studentManagement.filter.fields.gradeLevel.options.all')}</SelectItem>
                      {uniqueGradeLevels.map((grade) => (
                        <SelectItem
                          key={grade || 'no-grade'}
                          value={grade || 'no-grade'}
                        >
                          {grade || t('student-folder.studentManagement.filter.fields.gradeLevel.options.noGrade')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gender-filter">{t('student-folder.studentManagement.filter.fields.gender.label')}</Label>
                  <Select
                    onValueChange={(value) =>
                      handleFilterChange('gender', value, `${t('student-folder.studentManagement.filter.fields.gender.label')}: ${value}`)
                    }
                    defaultValue="all"
                  >
                    <SelectTrigger id="gender-filter">
                      <SelectValue placeholder={t('student-folder.studentManagement.filter.fields.gender.label')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('student-folder.studentManagement.filter.fields.gender.options.all')}</SelectItem>
                      <SelectItem value="male">{t('student-folder.studentManagement.filter.fields.gender.options.male')}</SelectItem>
                      <SelectItem value="female">{t('student-folder.studentManagement.filter.fields.gender.options.female')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="isPaid-filter">{t('student-folder.studentManagement.filter.fields.isPaid.label')}</Label>
                  <Select
                    onValueChange={(value) =>
                      handleFilterChange(
                        'isPaid',
                        value,
                        `${t('student-folder.studentManagement.filter.fields.isPaid.label')}: ${value === 'true' ? t('student-folder.studentManagement.filter.fields.isPaid.options.paid') : t('student-folder.studentManagement.filter.fields.isPaid.options.unpaid')}`
                      )
                    }
                    defaultValue="all"
                  >
                    <SelectTrigger id="isPaid-filter">
                      <SelectValue placeholder={t('student-folder.studentManagement.filter.fields.isPaid.label')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('student-folder.studentManagement.filter.fields.isPaid.options.all')}</SelectItem>
                      <SelectItem value="true">{t('student-folder.studentManagement.filter.fields.isPaid.options.paid')}</SelectItem>
                      <SelectItem value="false">{t('student-folder.studentManagement.filter.fields.isPaid.options.unpaid')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {/* Class Filter */}
                <div className="space-y-2">
                  <Label htmlFor="class-filter">{t('student-folder.studentManagement.filter.fields.class.label')}</Label>
                  <Select
                    onValueChange={(value) =>
                      handleFilterChange('class', value, `${t('student-folder.studentManagement.filter.fields.class.label')}: ${value}`)
                    }
                    defaultValue="all"
                  >
                    <SelectTrigger id="class-filter">
                      <SelectValue placeholder={t('student-folder.studentManagement.filter.fields.class.label')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('student-folder.studentManagement.filter.fields.class.options.all')}</SelectItem>
                      {uniqueClasses.map((className) => (
                        <SelectItem
                          key={className || 'no-class'}
                          value={className || 'no-class'}
                        >
                          {className || t('student-folder.studentManagement.filter.fields.class.options.noClass')}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {/* Parent Filter */}
                <div className="space-y-2">
                  <Label htmlFor="parent-filter">{t('student-folder.studentManagement.filter.fields.parent.label')}</Label>
                  <div className="relative">
                    <Input
                      id="parent-search"
                      placeholder={t('student-folder.studentManagement.filter.fields.parent.placeholder')}
                      className="mb-2"
                      value={parentSearchQuery}
                      onChange={(e) => setParentSearchQuery(e.target.value)}
                    />
                  </div>
                  <Select
                    onValueChange={(value) =>
                      handleFilterChange(
                        'parentId',
                        value,
                        `${t('student-folder.studentManagement.filter.fields.parent.label')}: ${parents?.find((p) => p.id === value)?.firstname || ''} ${parents?.find((p) => p.id === value)?.lastname || ''}`
                      )
                    }
                    defaultValue="all"
                  >
                    <SelectTrigger id="parent-filter">
                      <SelectValue placeholder={t('student-folder.studentManagement.filter.fields.parent.label')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('student-folder.studentManagement.filter.fields.parent.options.all')}</SelectItem>
                      {filteredParents?.map((parent) => (
                        <SelectItem key={parent.id} value={parent.id}>
                          {parent.firstname} {parent.lastname}{' '}
                          {parent.cin ? `(${parent.cin})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {activeFilters.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {activeFilters.map((filter) => (
            <Badge
              key={filter.key}
              variant="secondary"
              className="px-3 py-1 gap-2"
            >
              {filter.label}
              <button
                onClick={() =>
                  setActiveFilters((prev) =>
                    prev.filter((f) => f.key !== filter.key)
                  )
                }
                className="text-xs rounded-full hover:bg-gray-200 h-4 w-4 inline-flex items-center justify-center"
              >
                <X className="h-3 w-3" />
              </button>
            </Badge>
          ))}
          <Button
            variant="ghost"
            size="sm"
            onClick={clearAllFilters}
            className="h-7"
          >
            {t('student-folder.studentManagement.filter.clearAll')}
          </Button>
        </div>
      )}

      <div className="mt-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('student-folder.studentManagement.table.headers.student')}</TableHead>
              <TableHead>{t('student-folder.studentManagement.table.headers.cin')}</TableHead>
              <TableHead>{t('student-folder.studentManagement.table.headers.gradeLevel')}</TableHead>
              <TableHead>{t('student-folder.studentManagement.table.headers.class')}</TableHead>
              <TableHead>{t('student-folder.studentManagement.table.headers.payment')}</TableHead>
              <TableHead>{t('student-folder.studentManagement.table.headers.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>{currentStudents.map(renderStudentRow)}</TableBody>
        </Table>
      </div>

      <div className="mt-6 flex justify-center">
        <Button
          variant="outline"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <ChevronLeft className="mr-2 h-4 w-4" />
          {t('student-folder.studentManagement.pagination.previous')}
        </Button>
        <Button
          variant="outline"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          {t('student-folder.studentManagement.pagination.next')}
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {/* Student Profile Modal */}
      <Dialog open={isProfileModalOpen} onOpenChange={setIsProfileModalOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('student-folder.studentManagement.modals.profile.title')}</DialogTitle>
          </DialogHeader>
          {selectedStudent && (
            <StudentForm
              initialData={selectedStudent}
              parents={parents as Parent[]}
              classes={classes as Class[]}
              onSubmit={handleUpdateStudent}
              onCancel={() => setIsProfileModalOpen(false)}
              submitLabel={t('student-folder.studentManagement.buttons.updateStudent')}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Add New Student Modal */}
      <Dialog open={isAddStudentOpen} onOpenChange={setIsAddStudentOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('student-folder.studentManagement.modals.add.title')}</DialogTitle>
          </DialogHeader>
          <StudentForm
            parents={parents as Parent[]}
            classes={classes as Class[]}
            onSubmit={handleCreateStudent}
            onCancel={() => setIsAddStudentOpen(false)}
            submitLabel={t('student-folder.studentManagement.modals.add.title')}
          />
        </DialogContent>
      </Dialog>

      {/* Payment Status Confirmation Dialog */}
      <AlertDialog
        open={isPaymentDialogOpen}
        onOpenChange={setIsPaymentDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('student-folder.studentManagement.modals.payment.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {studentToTogglePaid && (
                <>
                  {t('student-folder.studentManagement.modals.payment.description', {
                    name: `${studentToTogglePaid.firstname} ${studentToTogglePaid.lastname}`,
                    status: studentToTogglePaid.isPaid
                      ? t('student-folder.studentManagement.filter.fields.isPaid.options.unpaid')
                      : t('student-folder.studentManagement.filter.fields.isPaid.options.paid'),
                  })}
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('student-folder.studentManagement.modals.payment.buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmTogglePayment}
              className={
                studentToTogglePaid?.isPaid
                  ? 'bg-red-600 hover:bg-red-700'
                  : 'bg-green-600 hover:bg-green-700'
              }
            >
              {studentToTogglePaid?.isPaid
                ? t('student-folder.studentManagement.buttons.markAsUnpaid')
                : t('student-folder.studentManagement.buttons.markAsPaid')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset All Students Confirmation Dialog */}
      <AlertDialog
        open={isResetDialogOpen}
        onOpenChange={(open) => !isResetting && setIsResetDialogOpen(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('student-folder.studentManagement.modals.reset.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('student-folder.studentManagement.modals.reset.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="reset-month">{t('student-folder.studentManagement.modals.reset.fields.month.label')}</Label>
              <Select
                value={resetMonth}
                onValueChange={setResetMonth}
                disabled={isResetting}
              >
                <SelectTrigger id="reset-month">
                  <SelectValue placeholder={t('student-folder.studentManagement.modals.reset.fields.month.placeholder')} />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((month) => (
                    <SelectItem key={month} value={month}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="reset-year">{t('student-folder.studentManagement.modals.reset.fields.year.label')}</Label>
              <Select
                value={resetYear}
                onValueChange={setResetYear}
                disabled={isResetting}
              >
                <SelectTrigger id="reset-year">
                  <SelectValue placeholder={t('student-folder.studentManagement.modals.reset.fields.year.placeholder')} />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {Array.from({ length: 101 }, (_, i) => (2000 + i).toString()).map((year) => (
                    <SelectItem key={year} value={year}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isResetting}>{t('student-folder.studentManagement.modals.reset.buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmResetAllStudents}
              className="bg-red-600 hover:bg-red-700"
              disabled={isResetting}
            >
              {isResetting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {t('student-folder.studentManagement.modals.reset.buttons.resetting')}
                </>
              ) : (
                t('student-folder.studentManagement.modals.reset.buttons.reset')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Download All Students Dialog */}
      <AlertDialog
        open={isAllStudentsDialogOpen}
        onOpenChange={(open) => !isDownloading && setIsAllStudentsDialogOpen(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('student-folder.studentManagement.modals.download.all.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('student-folder.studentManagement.modals.download.all.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="download-month">{t('student-folder.studentManagement.modals.download.fields.month.label')}</Label>
              <Select
                value={downloadMonth}
                onValueChange={setDownloadMonth}
                disabled={isDownloading}
              >
                <SelectTrigger id="download-month">
                  <SelectValue placeholder={t('student-folder.studentManagement.modals.download.fields.month.placeholder')} />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((month) => (
                    <SelectItem key={month} value={month}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="download-year">{t('student-folder.studentManagement.modals.download.fields.year.label')}</Label>
              <Input
                id="download-year"
                value={downloadYear}
                onChange={(e) => setDownloadYear(e.target.value)}
                disabled={isDownloading}
                placeholder="YYYY"
              />
            </div>
            <div className="space-y-2 col-span-2">
              <Label htmlFor="download-filename">{t('student-folder.studentManagement.modals.download.fields.filename.label')}</Label>
              <Input
                id="download-filename"
                value={downloadFilename}
                onChange={(e) => setDownloadFilename(e.target.value)}
                disabled={isDownloading}
                placeholder={t('student-folder.studentManagement.modals.download.fields.filename.placeholder')}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDownloading}>{t('student-folder.studentManagement.modals.download.buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDownloadAllStudents}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {t('student-folder.studentManagement.modals.download.buttons.downloading')}
                </>
              ) : (
                t('student-folder.studentManagement.modals.download.buttons.download')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Download Paid Students Dialog */}
      <AlertDialog
        open={isPaidStudentsDialogOpen}
        onOpenChange={(open) => !isDownloading && setIsPaidStudentsDialogOpen(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('student-folder.studentManagement.modals.download.paid.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('student-folder.studentManagement.modals.download.paid.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="download-month-paid">{t('student-folder.studentManagement.modals.download.fields.month.label')}</Label>
              <Select
                value={downloadMonth}
                onValueChange={setDownloadMonth}
                disabled={isDownloading}
              >
                <SelectTrigger id="download-month-paid">
                  <SelectValue placeholder={t('student-folder.studentManagement.modals.download.fields.month.placeholder')} />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((month) => (
                    <SelectItem key={month} value={month}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="download-year-paid">{t('student-folder.studentManagement.modals.download.fields.year.label')}</Label>
              <Input
                id="download-year-paid"
                value={downloadYear}
                onChange={(e) => setDownloadYear(e.target.value)}
                disabled={isDownloading}
                placeholder="YYYY"
              />
            </div>
            <div className="space-y-2 col-span-2">
              <Label htmlFor="download-filename-paid">{t('student-folder.studentManagement.modals.download.fields.filename.label')}</Label>
              <Input
                id="download-filename-paid"
                value={downloadFilename}
                onChange={(e) => setDownloadFilename(e.target.value)}
                disabled={isDownloading}
                placeholder={t('student-folder.studentManagement.modals.download.fields.filename.placeholder')}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDownloading}>{t('student-folder.studentManagement.modals.download.buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDownloadPaidStudents}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {t('student-folder.studentManagement.modals.download.buttons.downloading')}
                </>
              ) : (
                t('student-folder.studentManagement.modals.download.buttons.download')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Download Unpaid Students Dialog */}
      <AlertDialog
        open={isUnpaidStudentsDialogOpen}
        onOpenChange={(open) => !isDownloading && setIsUnpaidStudentsDialogOpen(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('student-folder.studentManagement.modals.download.unpaid.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('student-folder.studentManagement.modals.download.unpaid.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="download-month-unpaid">{t('student-folder.studentManagement.modals.download.fields.month.label')}</Label>
              <Select
                value={downloadMonth}
                onValueChange={setDownloadMonth}
                disabled={isDownloading}
              >
                <SelectTrigger id="download-month-unpaid">
                  <SelectValue placeholder={t('student-folder.studentManagement.modals.download.fields.month.placeholder')} />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((month) => (
                    <SelectItem key={month} value={month}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="download-year-unpaid">{t('student-folder.studentManagement.modals.download.fields.year.label')}</Label>
              <Input
                id="download-year-unpaid"
                value={downloadYear}
                onChange={(e) => setDownloadYear(e.target.value)}
                disabled={isDownloading}
                placeholder="YYYY"
              />
            </div>
            <div className="space-y-2 col-span-2">
              <Label htmlFor="download-filename-unpaid">{t('student-folder.studentManagement.modals.download.fields.filename.label')}</Label>
              <Input
                id="download-filename-unpaid"
                value={downloadFilename}
                onChange={(e) => setDownloadFilename(e.target.value)}
                disabled={isDownloading}
                placeholder={t('student-folder.studentManagement.modals.download.fields.filename.placeholder')}
              />
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDownloading}>{t('student-folder.studentManagement.modals.download.buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDownloadUnpaidStudents}
              disabled={isDownloading}
            >
              {isDownloading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {t('student-folder.studentManagement.modals.download.buttons.downloading')}
                </>
              ) : (
                t('student-folder.studentManagement.modals.download.buttons.download')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Student Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={(open) => !isDeleting && setIsDeleteDialogOpen(open)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('student-folder.studentManagement.modals.delete.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('student-folder.studentManagement.modals.delete.description', {
                name: studentToDelete ? `${studentToDelete.firstname} ${studentToDelete.lastname}` : ''
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>{t('student-folder.studentManagement.modals.delete.buttons.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteStudent}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  {t('student-folder.studentManagement.modals.delete.buttons.deleting')}
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('student-folder.studentManagement.modals.delete.buttons.delete')}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add the tour component at the end */}
      <StudentManagementTour />
    </div>
  )
}

